import { useMemo, CSSProperties } from "react";

import { SwitchControlledInput } from "@/common/components/SwitchControlled/SwitchControlled.tsx";
import { useCalculate } from "@/modules/dwelling/containers/DwellingEstimateContainer/Calculate/useCalculate.tsx";

import { CellInput, CellSelect, CommonCell } from "../CommonCell/CommonCell";
import {
  CostingCleaning,
  CostingToolCleaningPlanAllDwellingSlug,
  DwellingCostCleaningPlan,
  DwellingCostCleaningPlansResponse,
} from "../types";


export type FRow = CostingCleaning;

export default function useDwellingCleaningEstimateTable(
  data: CostingCleaning[],
  cleaningPlans: DwellingCostCleaningPlansResponse,
  name: string,
) {
  type Column = {
    header: string;
    field: string;
    body: (row: FRow) => JSX.Element;
    style?: CSSProperties;
  };

  const {
    costingCalculate,
    handleChangePreTaxPrice,
    handleChangeTaxPercentage,
    handleChangeSelect,
    handleChangeSelectRow,
    handleChangeQuantity,
    unselectEveryRow,
    unselectAllDwelling,
  } = useCalculate();

  const cleaningOptions = useMemo(
    () =>
      cleaningPlans.map((plan) => ({
        label: plan.label,
        value: plan.id,
        data: plan.stringTypes,
      })),
    [cleaningPlans],
  );

  const getPlanByOptionSelected = (option: {
    value: string;
    label: string;
  }): DwellingCostCleaningPlan => {
    const plan = cleaningPlans.find((plan) => plan.id === option.value);

    if (!plan) {
      throw new Error("Plan not found");
    }

    return plan;
  };

  const cleaningTable = [
    {
      header: "",
      field: "",
      body: (row: FRow) => {
        return (
          <SwitchControlledInput
            checked={
              costingCalculate.elements[name].selected[data.indexOf(row)]
            }
            onChangeCallback={() => {
              handleChangeSelectRow(name, data.indexOf(row));
              if (row.plan?.slug === CostingToolCleaningPlanAllDwellingSlug) {
                unselectEveryRow(name);
              } else {
                unselectAllDwelling(name);
              }
            }}
            name={`${row.id}_${data.indexOf(row)}_${row.roomName}_isCleaningSelected`}
          />
        );
      },
    },
    {
      header: "Pièce",
      field: "cleaningRoomName",
      body: (row: FRow) => <CommonCell>{row.roomName}</CommonCell>,
    },
    {
      header: "Observations",
      style: { paddingInlineEnd: 0 },
      field: "cleaningObservations",
      body: (row: FRow) => (
        <CellInput
          defaultValue={row.plan?.types || "-"}
          name={`${row.id}_${data.indexOf(row)}_${row.roomName}_observations`}
        />
      ),
    },
    {
      header: "Forfait",
      field: "cleaningPlanId",
      body: (row: FRow) => {
        const value = costingCalculate.elements[name].rows[data.indexOf(row)]
          .plan?.id
          ? {
              value:
                costingCalculate.elements[name].rows[data.indexOf(row)].plan
                  ?.id,
              label:
                costingCalculate.elements[name].rows[data.indexOf(row)].plan
                  ?.label,
            }
          : undefined;
        return (
          <CellSelect
            tooltipText={value?.label || ""}
            name={`${row.id}_${data.indexOf(row)}_${row.roomName}_plan`}
            onChange={(optSelected: { label: string; value: string }) => {
              const plan = getPlanByOptionSelected(optSelected);
              handleChangeSelect(name, data.indexOf(row), plan);
            }}
            placeholder="Forfait"
            value={value}
            defaultValue={"Forfait"}
            options={cleaningOptions}
          />
        );
      },
    },
    {
      header: "Quantité",
      field: "cost-quantity",
      body: (row: FRow) => (
        <CellInput
          min={0}
          type="number"
          name={`${row.id}_${data.indexOf(row)}_${row.roomName}_quantity`}
          onChange={(e) =>
            handleChangeQuantity(name, data.indexOf(row), e.target.value)
          }
          value={
            costingCalculate.elements[name].rows[data.indexOf(row)]?.quantity
          }
        />
      ),
    },
    {
      header: "Unité",
      field: "unit",
      body: (row: FRow) => {
        return (
          <CellInput
            type="text"
            defaultValue={row.plan?.unit || "H"}
            name={`${row.id}_${data.indexOf(row)}_${row.roomName}_unit`}
            style={{
              maxWidth: "30px",
            }}
          />
        );
      },
    },
    {
      header: "PU HT",
      field: "priceTaxExcluded",
      body: (row: FRow) => (
        <CellInput
          type="number"
          min={0}
          extension="€"
          onChange={(e) =>
            handleChangePreTaxPrice(name, data.indexOf(row), e.target.value)
          }
          value={
            costingCalculate.elements[name].rows[data.indexOf(row)]?.preTaxPrice
          }
        />
      ),
    },
    {
      header: "TVA",
      field: "taxPercentage",
      body: (row: FRow) => (
        <CellInput
          min={0}
          max={100}
          type="number"
          extension="%"
          onChange={(e) =>
            handleChangeTaxPercentage(name, data.indexOf(row), e.target.value)
          }
          value={
            costingCalculate.elements[name].rows[data.indexOf(row)]
              ?.taxPercentage
          }
        />
      ),
    },
    {
      header: "Montant TTC",
      field: "priceTaxIncluded",
      body: (row: FRow) => (
        <CommonCell>
          {costingCalculate.elements[name].rows[data.indexOf(row)]
            ?.taxIncludedPrice + "€"}
        </CommonCell>
      ),
    },
  ] satisfies Column[];

  return {
    cleaningTable,
  };
}
